import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum, PersonStatus_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import {
  generateRandomBirthDate,
  generateRandomDate,
  generateRandomGender,
  generateRandomInt,
  generateRandomPhoneNumber,
  generateRandomUserNames,
  generateRandomUUID,
  getRandomElement,
} from '@frontend/demo-interceptors';
import { type Person as PersonLegacy, PersonSourceType } from './types';
import { convertPersonV3ToPerson } from './v3/helpers';

export type DemoPresetPerson = 'jane-doe' | 'arthur-morgan';

export const EmptyPerson: Person = {
  personId: '',
  firstName: '',
  lastName: '',
  preferredName: '',
  gender: '',
  sourceId: '',
  sourceTenantId: '',
  personPmid: '',
  householdId: '',
  householdPmid: '',
  isGuardian: false,
  createdAt: '',
  modifiedAt: '',
};

const generatePersonRecord = (overrides: Partial<Person> & { email?: string; phone?: string }): Person => {
  const pmId = overrides.personPmid ?? `${generateRandomInt(1000, 9999)}`;
  const sourceId = overrides.sourceId ?? generateRandomUUID();
  const personId = overrides.personId ?? generateRandomUUID();
  const contactInfoId = generateRandomUUID();
  const householdId = overrides.householdId ?? generateRandomUUID();
  const { firstName = 'John', lastName = 'Doe' } = overrides;

  return {
    firstName,
    lastName,
    address: {
      city: overrides.address?.city || getRandomElement(['New York', 'Los Angeles', 'Chicago']),
      state: overrides.address?.state || getRandomElement(['NY', 'CA', 'IL']),
      street: overrides.address?.street || getRandomElement(['123 Main St', '456 Elm St', '789 Oak St']),
      postalCode: overrides.address?.postalCode || getRandomElement(['10001', '90001', '60601']),
    },
    sourceTenantId: sourceId,
    personPmid: pmId,
    personDisplayPmid: pmId,
    personId,
    status: overrides.status || PersonStatus_Enum.ACTIVE,
    householdId,
    householdPmid: `pm_${householdId}`,
    isGuardian: overrides.isGuardian ?? Math.random() < 0.2,
    sourceId,
    contactInfo: [
      {
        sourceId,
        contactInfoId,
        contactInfoPmid: `email_${pmId}`,
        personId,
        type: ContactType_Enum.EMAIL,
        destination: overrides.email ?? `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`,
        priority: 0,
      },
      {
        sourceId,
        contactInfoId,
        contactInfoPmid: `home_phone_${pmId}`,
        personId,
        type: ContactType_Enum.PHONE_HOME,
        destination: overrides.phone ?? generateRandomPhoneNumber(),
        priority: 0,
      },
      {
        sourceId,
        contactInfoId,
        contactInfoPmid: `office_phone_${pmId}`,
        personId,
        type: ContactType_Enum.PHONE_WORK,
        destination: overrides.phone ?? generateRandomPhoneNumber(),
        priority: 0,
      },
    ],
    birthdate: overrides.birthdate || generateRandomBirthDate(),
    createdAt: overrides.createdAt || generateRandomDate(),
    modifiedAt: overrides.modifiedAt || generateRandomDate(),
    gender: overrides.gender ?? generateRandomGender(),
  };
};

export const DEMO_PRESET_PERSONS: Record<DemoPresetPerson, Person> = {
  'jane-doe': generatePersonRecord({
    firstName: 'Jane',
    lastName: 'Doe',
    gender: 'Female',
    birthdate: '1990-01-01T00:00:00Z',
    address: {
      street: '123 Main St',
      city: 'New York',
      state: 'NY',
      postalCode: '10001',
    },
    isGuardian: false,
    householdId: '',
    email: 'janedoe@gmail.com',
    phone: '5551234567',
    // This is a fixed personId which is used in call intel demo data for patient insight
    personId: 'demo-d5cb44b7-177d-48ff-b7a0-31c308745223',
  }),
  'arthur-morgan': generatePersonRecord({
    firstName: 'Arthur',
    lastName: 'Morgan',
    gender: 'Male',
    birthdate: '1989-01-01T00:00:00Z',
    address: {
      street: '456 Elm St',
      city: 'Los Angeles',
      state: 'CA',
      postalCode: '90001',
    },
    email: 'arthurmorgan@gmail.com',
    phone: '5551234568',
  }),
};

export class MockPersons {
  persons: Person[] = [];

  constructor({ count, persons }: { count: number; persons?: never } | { count?: never; persons: Person[] }) {
    if (persons) {
      this.persons = persons;
    } else if (count) {
      this.generateRandomPersons(count);
    }
  }

  generateRandomPersons(count: number): Person[] {
    const userNamesList = generateRandomUserNames(count);
    // generate list of household ids to group max 3 persons in a household
    const householdIds = Array.from({ length: Math.round(count / 3) }).map(() => generateRandomUUID());

    this.persons = userNamesList.map<Person>(({ firstName, lastName }, index) => {
      return generatePersonRecord({
        firstName,
        lastName,
        householdId: householdIds.length ? householdIds[index % householdIds.length] : undefined,
      });
    });

    return this.persons;
  }

  getFilteredPersons(filters: { personId?: string; householdId?: string }): Person[] {
    // Include preset persons in the filtering logic
    const allPersons = Object.values(DEMO_PRESET_PERSONS).concat(this.persons);
    return allPersons.filter((person) => {
      if (filters.personId && person.personId !== filters.personId) {
        return false;
      }
      if (filters.householdId && person.householdId !== filters.householdId) {
        return false;
      }
      return true;
    });
  }

  getPersonExtended(filters: { personId?: string; householdId?: string }): PersonLegacy {
    return {
      ...convertPersonV3ToPerson(this.getFilteredPersons(filters)[0]),
      SourceType: PersonSourceType.Integration,
    };
  }

  getPersonV3(filters: { personId?: string; householdId?: string }): Person | undefined {
    return this.getFilteredPersons(filters)[0];
  }
}
